import Grid from '@material-ui/core/Grid';
import React, { ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions } from '~/store/hooks';
import Button from '~/ui/components/common/Button';
import Loader from '~/ui/components/common/Loader';
import Input from '~/ui/components/inputs/Input';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import { extractErrorMessage } from '~/utils/error/error';
import { secondaryDiagnosisValidationSchema } from './validates';

import { IDetails } from '../types';
import styles from './Styles.module.scss';
import useDiagnoses from './hooks/useDiagnoses';
import { IRequestInfo, ISecondaryDiagnosisInitialValues } from './types';

interface IProps {
  showSecondSubmit: boolean;
  setModalTitle: (v: string) => void;
  setDetails: (v: IDetails) => void;
  defaultValues: ISecondaryDiagnosisInitialValues;
  details: IDetails | null;
  requestInfo: IRequestInfo;
}

const AddSecondaryDiagnosis = ({
  showSecondSubmit,
  setModalTitle,
  setDetails,
  defaultValues,
  details,
  requestInfo,
}: IProps): ReactElement => {
  const {
    reset,
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm({ defaultValues, resolver: secondaryDiagnosisValidationSchema });

  const { loading, diagnosisOptions } = useDiagnoses();

  const setCurrent = useStoreActions(actions => actions.clientDetails.setCurrent);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onCreateSecondaryDiagnosis, onUpdateSecondaryDiagnosis } = useStoreActions(
    actions => actions.clientDetails,
  );

  const onSubmit = async (
    vals: ISecondaryDiagnosisInitialValues,
    evt: React.BaseSyntheticEvent,
  ) => {
    const { name } = (evt as React.BaseSyntheticEvent<HTMLFormElement>).nativeEvent.submitter;
    try {
      if (details && details.id) {
        const payload = {
          requestInfo: { ...requestInfo, id: String(details.id) },
          requestPayload: vals,
        };

        onUpdateSecondaryDiagnosis(payload);

        setDetails(null);
        setCurrent(null);
      } else {
        const payload = {
          requestInfo,
          requestPayload: vals,
        };

        await onCreateSecondaryDiagnosis(payload);

        reset({ id: null, note: '' });
      }
      setModalTitle(name);
      const type = details?.id ? 'update' : 'added';
      showNotify({ message: `Secondary diagnosis successfully ${type}` });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  if (loading) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Select
            isRelativeWindow
            name="id"
            options={diagnosisOptions}
            control={control}
            errors={errors}
            label="Select Secondary Dx"
            isDisabled={!showSecondSubmit}
            hideSelectedOptions={false}
          />
        </Grid>
        <Grid item sm={12}>
          <Input
            size="medium"
            name="note"
            register={register}
            label="Secondary Dx Note"
            multiline
            errors={errors}
          />
        </Grid>
      </Grid>
      <div className={styles.buttonsWrapper}>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => {
            setDetails(null);
            setModalTitle(null);
            setCurrent(null);
          }}
        >
          Cancel
        </Button>
        {showSecondSubmit && (
          <Button
            color="primary"
            variant="outlined"
            type="submit"
            name="Add Secondary Diagnosis"
            className={styles.margin}
          >
            Save and Add another
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          type="submit"
          name={null}
          className={!showSecondSubmit ? styles.margin : ''}
        >
          {showSecondSubmit ? 'Save' : 'Update'}
        </Button>
      </div>
    </form>
  );
};

export default AddSecondaryDiagnosis;
